// client/src/components/reports/ScheduleModal.jsx
//
// Schedule a saved report: daily / weekly / monthly at an hour in the
// account's time zone, CSV or PDF, to staff of this account. Recipients get a
// push and an email with a sign-in link; the file itself is never attached.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listSchedules, createSchedule, updateSchedule, deleteSchedule, getRecipients, errorText,
} from '../../services/reportsApi';

const EMPTY = { cadence: 'weekly', runWeekday: 1, runMonthday: 1, runHour: 7, format: 'pdf', recipients: [] };

export function hourLabel(h) {
  const d = new Date(2000, 0, 1, h);
  return d.toLocaleTimeString(undefined, { hour: 'numeric' });
}

export default function ScheduleModal({ reportId, reportName, timezone, onClose }) {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState(null);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dialog = useRef(null);

  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((d) => new Date(2023, 0, 1 + d).toLocaleDateString(undefined, { weekday: 'long' }));
  const cadenceLabel = {
    daily: t('reports.schedule.daily', 'Daily'),
    weekly: t('reports.schedule.weekly', 'Weekly'),
    monthly: t('reports.schedule.monthly', 'Monthly'),
  };

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([listSchedules(reportId), getRecipients()]);
      setSchedules(s.schedules);
      setStaff(r.recipients);
    } catch (err) {
      setError(errorText(err, t('reports.schedule.loadError', 'Could not load schedules.')));
      setSchedules([]);
    }
  }, [reportId, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    dialog.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleRecipient = (id) => setForm((f) => ({
    ...f,
    recipients: f.recipients.includes(id) ? f.recipients.filter((x) => x !== id) : [...f.recipients, id],
  }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createSchedule(reportId, form);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(errorText(err, t('reports.schedule.saveError', 'Could not save the schedule.')));
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (s, active) => {
    try {
      await updateSchedule(reportId, s.id, { ...s, active });
      await load();
    } catch (err) {
      setError(errorText(err, t('reports.schedule.saveError', 'Could not save the schedule.')));
    }
  };

  const remove = async (s) => {
    try {
      await deleteSchedule(reportId, s.id);
      await load();
    } catch (err) {
      setError(errorText(err, t('reports.schedule.deleteError', 'Could not delete the schedule.')));
    }
  };

  const describe = (s) => {
    const when = s.cadence === 'weekly' ? weekdays[s.runWeekday]
      : s.cadence === 'monthly' ? t('reports.schedule.onDay', 'day {{day}}', { day: s.runMonthday })
        : '';
    return `${cadenceLabel[s.cadence]}${when ? ` · ${when}` : ''} · ${hourLabel(s.runHour)} · ${s.format.toUpperCase()} · ${t('reports.schedule.recipientCount', '{{count}} recipient(s)', { count: s.recipients.length })}`;
  };

  return (
    <div className="rp-overlay" role="presentation" onClick={onClose}>
      <div
        className="rp-modal surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-sched-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="rp-sched-title">{t('reports.schedule.title', 'Schedule “{{name}}”', { name: reportName })}</h3>
        <p className="surface-muted perf-small" style={{ margin: 0 }}>
          {t('reports.schedule.intro', 'Recipients get a notification and an email with a sign-in link. Times are in {{tz}}.', { tz: timezone || t('reports.schedule.accountTz', "your account's time zone") })}
        </p>

        {schedules && schedules.length > 0 && (
          <ul className="rp-sched-list">
            {schedules.map((s) => (
              <li key={s.id} className="rp-run">
                <span>{describe(s)}{!s.active && ` · ${t('reports.schedule.paused', 'paused')}`}</span>
                <span className="rp-actions">
                  <button type="button" className="lm-btn secondary" onClick={() => setActive(s, !s.active)}>
                    {s.active ? t('reports.schedule.pause', 'Pause') : t('reports.schedule.resume', 'Resume')}
                  </button>
                  <button type="button" className="rp-icon-btn" onClick={() => remove(s)}>
                    {t('reports.schedule.delete', 'Delete')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <form className="fo-form-grid" onSubmit={save}>
          <label className="fo-field">
            {t('reports.schedule.cadence', 'How often')}
            <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}>
              {['daily', 'weekly', 'monthly'].map((c) => <option key={c} value={c}>{cadenceLabel[c]}</option>)}
            </select>
          </label>
          {form.cadence === 'weekly' && (
            <label className="fo-field">
              {t('reports.schedule.weekday', 'Day')}
              <select value={form.runWeekday} onChange={(e) => setForm({ ...form, runWeekday: Number(e.target.value) })}>
                {weekdays.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
          )}
          {form.cadence === 'monthly' && (
            <label className="fo-field">
              {t('reports.schedule.monthday', 'Day of month')}
              <select value={form.runMonthday} onChange={(e) => setForm({ ...form, runMonthday: Number(e.target.value) })}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className="fo-field">
            {t('reports.schedule.hour', 'Time')}
            <select value={form.runHour} onChange={(e) => setForm({ ...form, runHour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </label>
          <label className="fo-field">
            {t('reports.schedule.format', 'Format')}
            <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
            </select>
          </label>
          <fieldset className="fo-field" style={{ gridColumn: '1 / -1', border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontSize: '0.8rem', color: '#23324d' }}>{t('reports.schedule.recipients', 'Send to (staff of this account)')}</legend>
            <ul className="rp-recipients">
              {staff.map((u) => (
                <li key={u.id}>
                  <label>
                    <input type="checkbox" checked={form.recipients.includes(u.id)} onChange={() => toggleRecipient(u.id)} />
                    <span>{u.name} <small>{u.email}</small></span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
          {error && <div className="note error" role="alert" style={{ gridColumn: '1 / -1' }}>{error}</div>}
          <div className="rp-actions" style={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
            <button type="button" className="lm-btn secondary" onClick={onClose}>{t('reports.schedule.close', 'Close')}</button>
            <button type="submit" className="lm-btn" disabled={saving || form.recipients.length === 0}>
              {t('reports.schedule.add', 'Add schedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
